import { useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { CheckboxField } from '@/components/checkbox-field';
import { LEGAL_URLS } from '@/components/legal-links';
import type { Copy } from '@/lib/messages';

export type LegalDocument = 'terms' | 'privacy';

interface RequireTermsStepProps {
  copy: Copy;
  pending: boolean;
  error: string | null;
  /** Only what is actually outstanding — a republished privacy policy does
   *  not ask someone to re-agree to terms they already accepted. */
  documents: LegalDocument[];
  onSubmit: () => void;
}

/**
 * The mandatory Terms of Use / Privacy Policy step
 * (docs/auth/40-features/terms-acceptance.md).
 *
 * No skip: the login is proven but held server-side, and no token is issued
 * until both are accepted. Declining is leaving — the footer's "cancel" link
 * aborts the authorization, which writes nothing to the account.
 */
export function RequireTermsStep({
  copy,
  pending,
  error,
  documents,
  onSubmit,
}: RequireTermsStepProps) {
  const [accepted, setAccepted] = useState<Record<LegalDocument, boolean>>({
    terms: false,
    privacy: false,
  });

  // A document that is not outstanding is not shown, and must not gate the
  // button — the server would not record it either.
  const complete = documents.every((document) => accepted[document]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!complete) return;
    onSubmit();
  }

  function link(document: LegalDocument) {
    return (
      <a
        href={LEGAL_URLS[document]}
        target="_blank"
        rel="noreferrer noopener"
        className="focus-ring rounded underline hover:text-buds-dark"
      >
        {document === 'terms' ? copy.legalTerms : copy.legalPrivacy}
      </a>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {documents.includes('terms') ? (
        <CheckboxField
          id="accept-terms"
          checked={accepted.terms}
          onChange={(event) =>
            setAccepted((prev) => ({ ...prev, terms: event.target.checked }))
          }
          label={
            <>
              {copy.termsCheckboxBefore}
              {link('terms')}
              {copy.termsCheckboxAfter}
            </>
          }
        />
      ) : null}

      {documents.includes('privacy') ? (
        <CheckboxField
          id="accept-privacy"
          checked={accepted.privacy}
          onChange={(event) =>
            setAccepted((prev) => ({ ...prev, privacy: event.target.checked }))
          }
          label={
            <>
              {copy.privacyCheckboxBefore}
              {link('privacy')}
              {copy.privacyCheckboxAfter}
            </>
          }
        />
      ) : null}

      <Button type="submit" loading={pending} disabled={pending || !complete}>
        {copy.termsRequiredSubmit}
      </Button>
    </form>
  );
}
